import React, { useMemo, useState } from "react";
import { Search, Tag, X } from "lucide-react";
import { COMMON_CRM_VARIABLES } from "./constants";
import type { TriggerItem } from "./types";

interface VariablePickerProps {
  title: string;
  description?: string;
  triggerItem?: TriggerItem;
  onSelect: (tag: string) => void;
}

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

/**
 * Lista pesquisável e categorizada de variáveis dinâmicas ({{item.campo}}).
 * Exibe apenas o nome amigável com a tag técnica no tooltip.
 * Suporta variáveis do negócio conectado quando a automação for de atividade.
 */
export const VariablePicker: React.FC<VariablePickerProps> = ({
  title,
  description,
  triggerItem,
  onSelect,
}) => {
  const [query, setQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState<string>("todos");

  // Ordenação prioritária dos grupos conforme o gatilho da automação
  const groupPriority = useMemo(() => {
    if (triggerItem === "atividade") {
      return [
        "Negócio Conectado",
        "Atividade",
        "Contato Conectado",
        "Empresa Conectada",
        "Geral",
        "Histórico",
        "Negócio",
        "Pessoa",
      ];
    }
    if (triggerItem === "negocio") {
      return [
        "Negócio",
        "Contato Conectado",
        "Empresa Conectada",
        "Geral",
        "Histórico",
        "Negócio Conectado",
        "Atividade",
        "Pessoa",
      ];
    }
    return [
      "Negócio Conectado",
      "Negócio",
      "Atividade",
      "Contato Conectado",
      "Empresa Conectada",
      "Geral",
      "Histórico",
    ];
  }, [triggerItem]);

  // Grupos únicos existentes na lista
  const availableGroups = useMemo(() => {
    const set = new Set<string>();
    COMMON_CRM_VARIABLES.forEach((v) => set.add(v.group));
    return Array.from(set).sort((a, b) => {
      const idxA = groupPriority.indexOf(a);
      const idxB = groupPriority.indexOf(b);
      return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
    });
  }, [groupPriority]);

  // Lista ordenada e filtrada
  const filtered = useMemo(() => {
    const q = normalize(query.trim());

    return COMMON_CRM_VARIABLES.filter((v) => {
      if (selectedGroup !== "todos" && v.group !== selectedGroup) {
        return false;
      }
      if (!q) return true;
      return (
        normalize(v.label).includes(q) ||
        normalize(v.group).includes(q) ||
        normalize(v.tag).includes(q)
      );
    }).sort((a, b) => {
      const idxA = groupPriority.indexOf(a.group);
      const idxB = groupPriority.indexOf(b.group);
      return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
    });
  }, [query, selectedGroup, groupPriority]);

  return (
    <div className="p-3 rounded-lg border border-border/60 bg-muted/20 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
          <Tag className="w-3.5 h-3.5 text-primary" />
          <span>{title}</span>
        </div>
        {triggerItem === "atividade" && (
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
            Inclui dados do Negócio Conectado
          </span>
        )}
      </div>

      {description && (
        <p className="text-[11px] text-muted-foreground">{description}</p>
      )}

      {/* Barra de pesquisa */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
        <input
          id="automation-variable-search"
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pesquisar campo (ex: nome, etapa, valor, negócio)..."
          className="w-full h-8 pl-8 pr-7 text-xs rounded-md border border-border/80 bg-card outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary/60 transition-shadow"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-muted-foreground hover:text-foreground cursor-pointer"
            aria-label="Limpar pesquisa"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Filtros rápidos por Categoria / Grupo */}
      <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
        <button
          type="button"
          onClick={() => setSelectedGroup("todos")}
          className={`px-2 py-0.5 rounded-full font-medium transition-colors cursor-pointer shrink-0 ${
            selectedGroup === "todos"
              ? "bg-primary text-primary-foreground shadow-2xs"
              : "bg-card text-muted-foreground hover:text-foreground border border-border/70"
          }`}
        >
          Todos ({COMMON_CRM_VARIABLES.length})
        </button>
        {availableGroups.map((grp) => (
          <button
            key={grp}
            type="button"
            onClick={() =>
              setSelectedGroup(grp === selectedGroup ? "todos" : grp)
            }
            className={`px-2 py-0.5 rounded-full font-medium transition-colors cursor-pointer shrink-0 ${
              selectedGroup === grp
                ? "bg-primary text-primary-foreground shadow-2xs"
                : "bg-card text-muted-foreground hover:text-foreground border border-border/70"
            }`}
          >
            {grp}
          </button>
        ))}
      </div>

      {/* Lista de tags clicáveis com apenas o nome legível */}
      <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto pr-1">
        {filtered.length === 0 ? (
          <span className="text-[11px] text-muted-foreground py-2">
            Nenhum campo encontrado para &quot;{query}&quot;.
          </span>
        ) : (
          filtered.map((v) => (
            <button
              key={v.tag}
              type="button"
              onClick={() => onSelect(v.tag)}
              title={`${v.tag} (${v.group})`}
              className="px-2.5 py-1 text-[11px] font-medium rounded-md bg-card hover:bg-primary hover:text-primary-foreground border border-border/80 transition-colors cursor-pointer text-left"
            >
              {v.label}
            </button>
          ))
        )}
      </div>
    </div>
  );
};
